import { DOSSIERS_QUERY_KEY, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { Suggestie } from "@/entities/suggestie/domain";
import { queryClient, useApiClient } from "@/infrastructure/rest";
import { SuggestieEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { SuggestieDto } from "@/shared/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SUGGESTIES_QUERY_KEY, SuggestieStatus } from "./constants";

function getDossierSuggestiesQuery(id: string, apiClient: ReturnType<typeof useApiClient>) {
    return {
        queryKey: [DOSSIERS_QUERY_KEY, id, SUGGESTIES_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<SuggestieDto[]>(`/dossiers/${id}/suggesties`);
            return data?.map((dto) => Suggestie.fromDto(dto)) ?? [];
        },
    };
}

export function usePrefetchDossierSuggestiesQuery(dossierId: string) {
    const apiClient = useApiClient();
    const query = getDossierSuggestiesQuery(dossierId, apiClient);
    return () => queryClient.prefetchQuery({ ...query, staleTime: Infinity });
}

export function useGetDossierSuggestiesById(id: string) {
    const apiClient = useApiClient();
    const query = getDossierSuggestiesQuery(id, apiClient);

    return useQuery(query);
}

export function useRealtimeSuggesties() {
    const { create, update, remove } = useUpdateDossierPropertyQueriesData("suggesties");

    useRealtimeTopic<SuggestieEvent>("suggesties", {
        onMessage: (message) => {
            const { suggestie, dossierId, reason } = message;
            switch (reason) {
                case "Nieuw":
                    create(dossierId, () => Suggestie.fromDto(suggestie));
                    break;
                case "Wijziging":
                    update(dossierId, suggestie.id, () => Suggestie.fromDto(suggestie));
                    break;
                case "Verwijderd":
                    remove(dossierId, suggestie.id);
                    break;
            }
        },
    });
}

type ProcessSuggestieParams = {
    dossierId: string;
    suggestieId: number;
};

export function useWeigerSuggestie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, suggestieId }: ProcessSuggestieParams) => {
            await apiClient.patch(`/dossiers/${dossierId}/suggesties/${suggestieId}/status`, {
                status: SuggestieStatus.GENEGEERD,
            });
        },
        onError: (error) => {
            console.error("Error weiger suggestie:", error);
        },
    });
}

export function useCompleteSuggestie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, suggestieId }: ProcessSuggestieParams) => {
            await apiClient.patch(`/dossiers/${dossierId}/suggesties/${suggestieId}/status`, {
                status: SuggestieStatus.VOLDAAN,
            });
        },
        onError: (error) => {
            console.error("Error complete suggestie:", error);
        },
    });
}

export function useHerstelSuggestie() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, suggestieId }: ProcessSuggestieParams) => {
            await apiClient.patch(`/dossiers/${dossierId}/suggesties/${suggestieId}/status`, {
                status: SuggestieStatus.NIEUW,
            });
        },
        onError: (error) => {
            console.error("Error herstel suggestie:", error);
        },
    });
}
